import React, { useState } from 'react';
import { List, FormInstance, Button } from 'antd';
import {
  Icon,
  LoadingState,
  ErrorHandler,
  TextOverflow,
  Text,
  Col,
  Row,
} from '@common/components';
import { StoreValue, useQuery } from '@common/hooks';
import { OrganizationSupplierAttachmentsQuery } from '@institutions/graphql';
import { toUSFormat } from '@common/services/datetime';
import { Colors } from '@common/tokens';
import { SuppliersManagementCreateFileForm } from './suppliers-management-create-file-form';
import { SuppliersManagementDeleteFileModal } from './suppliers-management-delete-file-modal';

type Props = {
  form: FormInstance;
  supplierId: string;
  filesEditionMode: boolean;
  onFormSubmit: (value: Record<string, StoreValue>) => void;
};

export function SuppliersManagementFilesDrawer({
  supplierId,
  filesEditionMode,
  form,
  onFormSubmit,
}: Props) {
  const [deleteFileId, setDeleteFileId] = useState('');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [onHoverFileId, setOnHoverFileId] = useState('');

  const { data, loading, error } = useQuery(
    OrganizationSupplierAttachmentsQuery,
    {
      variables: { supplierId: supplierId },
    }
  );

  const onDownloadAllClick = () => {
    const urls = data?.organizationSupplierAttachments.map(
      (item) => item.file.downloadUrl
    );

    const download = (urls: string[]) => {
      const url = urls.pop() || '';

      const fileElement = document.createElement('a');
      fileElement.setAttribute('href', url);
      fileElement.setAttribute('download', '');
      fileElement.setAttribute('target', '_blank');
      fileElement.click();

      if (urls.length === 0) {
        clearInterval(interval);
      }
    };

    const interval = setInterval(download, 300, urls);
  };

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <ErrorHandler log="SuppliersManagementViewDrawerFiles" error={error} />
    );
  }

  return (
    <>
      {filesEditionMode ? (
        <SuppliersManagementCreateFileForm
          form={form}
          onFormSubmit={onFormSubmit}
        />
      ) : (
        <List
          className="supplier-management-files-drawer__files-list"
          header={
            <Row>
              <Col span={12}>Attached Documents</Col>
              <Col span={12}>
                <Row justify="end">
                  <a
                    data-testid="download-link"
                    onClick={onDownloadAllClick}
                    hidden={data?.organizationSupplierAttachments?.length === 0}
                  >
                    <Icon icon="arrowDownToBracket" kind="regular" />
                    <span style={{ marginLeft: 8 }}>Download all</span>
                  </a>
                </Row>
              </Col>
            </Row>
          }
          bordered
          dataSource={data?.organizationSupplierAttachments}
          renderItem={(item) => (
            <List.Item
              onMouseOver={() => setOnHoverFileId(item.id)}
              onMouseLeave={() => setOnHoverFileId('')}
              actions={[
                <Button
                  key={item.id}
                  hidden={onHoverFileId !== item.id}
                  type="link"
                  danger
                  onClick={() => {
                    setDeleteFileId(item.id);
                    setDeleteModalVisible(true);
                  }}
                >
                  Delete
                </Button>,
              ]}
              style={{ minHeight: 60 }}
            >
              <Col span={20}>
                <Row>
                  <a
                    data-testid="download-link"
                    href={item.file.downloadUrl || undefined}
                  >
                    <TextOverflow tooltip={item.fileName}>
                      {item.fileName.length > 70
                        ? `${item.fileName.slice(0, 70)}..`
                        : item.fileName}
                      {item.fileExtension}
                      <Icon
                        icon="arrowDownToBracket"
                        kind="regular"
                        className="download-tooltip"
                      />
                    </TextOverflow>
                  </a>
                </Row>
                {item.expiresOn && (
                  <Row>
                    {item.expired ? (
                      <Text color="coral">
                        Expired on {toUSFormat(item.expiresOn)}
                      </Text>
                    ) : (
                      <Text>Expires on {toUSFormat(item.expiresOn)}</Text>
                    )}
                  </Row>
                )}
              </Col>
            </List.Item>
          )}
        />
      )}
      <SuppliersManagementDeleteFileModal
        fileId={deleteFileId}
        visible={deleteModalVisible}
        onAction={(value) => setDeleteModalVisible(value)}
      />
      <style jsx>{`
        :global(
          .supplier-management-files-drawer__files-list .ant-list-item:hover
        ) {
          background-color: ${Colors.elmHyperlight};
        }
        :global(
          .supplier-management-files-drawer__files-list .download-tooltip
        ) {
          margin-left: 8px;
        }
      `}</style>
    </>
  );
}
